import { addDays, format, startOfWeek } from 'date-fns';
import type {
  DayPunchRecord,
  PunchActionType,
  PunchCorrectionReason,
  PunchEvent,
  TodayPunchStatus,
  WorkState,
} from '../types/employee';
import { WEEK_STARTS_ON, WORK_WEEKDAYS } from '../modules/shift-management/schedulingConfig';
import { formatPunchTime, todayDateKey } from './punchServiceDateUtils';
import type { PunchBlockReason } from './punchServiceTypes';

/**
 * A single employee id, or the alias list (Convex id plus the old `teacher-N`
 * legacyId). Punch events written before the Convex migration are still keyed
 * by the legacy id, so lookups must match either.
 */
export type EmployeeIdKey = string | readonly string[];

function employeeIdMatches(storedId: string, employeeId: EmployeeIdKey): boolean {
  if (typeof employeeId === 'string') return storedId === employeeId;
  return employeeId.includes(storedId);
}

/** The id to stamp on newly created events — always the primary (Convex) id. */
function primaryEmployeeId(employeeId: EmployeeIdKey): string {
  return typeof employeeId === 'string' ? employeeId : employeeId[0] ?? '';
}

export function getVoidedEventIds(events: PunchEvent[]): Set<string> {
  const voided = new Set<string>();
  for (const event of events) {
    if (event.type === 'void' && event.voidsEventId) {
      voided.add(event.voidsEventId);
    }
  }
  return voided;
}

export function getEmployeeEventsForDate(
  events: PunchEvent[],
  employeeId: EmployeeIdKey,
  dateKey = todayDateKey()
): PunchEvent[] {
  return events
    .filter(
      (e) =>
        employeeIdMatches(e.employeeId, employeeId) &&
        format(new Date(e.at), 'yyyy-MM-dd') === dateKey
    )
    .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
}

export function getEffectiveEventsForDate(
  events: PunchEvent[],
  employeeId: EmployeeIdKey,
  dateKey = todayDateKey()
): PunchEvent[] {
  const voided = getVoidedEventIds(events);
  return getEmployeeEventsForDate(events, employeeId, dateKey).filter(
    (e) => e.type !== 'void' && !voided.has(e.id)
  );
}

export function isEventVoided(events: PunchEvent[], eventId: string): boolean {
  return getVoidedEventIds(events).has(eventId);
}

export function deriveWorkState(effectiveEvents: PunchEvent[]): WorkState {
  let state: WorkState = 'none';
  for (const event of effectiveEvents) {
    switch (event.type) {
      case 'in':
        state = 'working';
        break;
      case 'lunch-start':
        state = 'on_lunch';
        break;
      case 'lunch-end':
        state = 'working';
        break;
      case 'out':
        state = 'done';
        break;
      default:
        break;
    }
  }
  return state;
}

export function buildTodayPunchStatus(
  events: PunchEvent[],
  employeeId: EmployeeIdKey,
  dateKey = todayDateKey()
): TodayPunchStatus {
  const effective = getEffectiveEventsForDate(events, employeeId, dateKey);
  let clockIn: string | null = null;
  let clockOut: string | null = null;
  let lunchStart: string | null = null;
  let lunchEnd: string | null = null;

  for (const event of effective) {
    if (event.type === 'in') clockIn = event.at;
    if (event.type === 'out') clockOut = event.at;
    if (event.type === 'lunch-start') lunchStart = event.at;
    if (event.type === 'lunch-end') lunchEnd = event.at;
  }

  const workState = deriveWorkState(effective);

  return {
    clockIn: clockIn ? formatPunchTime(clockIn) : null,
    clockOut: clockOut ? formatPunchTime(clockOut) : null,
    lunchStart: lunchStart ? formatPunchTime(lunchStart) : null,
    lunchEnd: lunchEnd ? formatPunchTime(lunchEnd) : null,
    workState,
    isCurrentlyIn: workState === 'working' || workState === 'on_lunch',
  };
}

export function getTodayClockBounds(
  events: PunchEvent[],
  employeeId: EmployeeIdKey,
  dateKey = todayDateKey()
): { clockInAt: string | null; clockOutAt: string | null } {
  const effective = getEffectiveEventsForDate(events, employeeId, dateKey);
  let clockInAt: string | null = null;
  let clockOutAt: string | null = null;
  for (const event of effective) {
    if (event.type === 'in') clockInAt = event.at;
    if (event.type === 'out') clockOutAt = event.at;
  }
  return { clockInAt, clockOutAt };
}

export function getTodayPunchStatus(
  events: PunchEvent[],
  employeeId: EmployeeIdKey,
  dateKey = todayDateKey()
): TodayPunchStatus {
  return buildTodayPunchStatus(events, employeeId, dateKey);
}

export function canPunch(
  events: PunchEvent[],
  employeeId: EmployeeIdKey,
  type: PunchActionType,
  dateKey = todayDateKey()
): { ok: true } | { ok: false; reason: PunchBlockReason } {
  const effective = getEffectiveEventsForDate(events, employeeId, dateKey);
  const state = deriveWorkState(effective);

  switch (type) {
    case 'in':
      if (state !== 'none') return { ok: false, reason: 'already-in' };
      return { ok: true };
    case 'lunch-start':
      if (state !== 'working') {
        return { ok: false, reason: state === 'on_lunch' ? 'on-lunch' : 'not-working' };
      }
      return { ok: true };
    case 'lunch-end':
      if (state !== 'on_lunch') {
        return { ok: false, reason: state === 'working' ? 'not-on-lunch' : 'not-working' };
      }
      return { ok: true };
    case 'out':
      if (state === 'none' || state === 'done') {
        return { ok: false, reason: 'out-without-in' };
      }
      if (state === 'on_lunch') return { ok: false, reason: 'on-lunch' };
      return { ok: true };
    default:
      return { ok: false, reason: 'not-working' };
  }
}

export function createPunchEvent(
  employeeId: EmployeeIdKey,
  type: PunchActionType,
  at = new Date().toISOString(),
  extras?: Pick<PunchEvent, 'earlyOutReason'>
): PunchEvent {
  return {
    id: `punch-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    employeeId: primaryEmployeeId(employeeId),
    type,
    at,
    source: 'office',
    ...extras,
  };
}

export function createVoidEvent(
  employeeId: string,
  voidsEventId: string,
  reason: PunchCorrectionReason,
  approvedByEmployeeId: string,
  reasonNote?: string
): PunchEvent {
  return {
    id: `void-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    employeeId,
    type: 'void',
    at: new Date().toISOString(),
    source: 'office',
    voidsEventId,
    reason,
    reasonNote,
    approvedByEmployeeId,
  };
}

export function createCorrectionPunchEvent(
  employeeId: string,
  type: PunchActionType,
  at: string,
  reason: PunchCorrectionReason,
  approvedByEmployeeId: string,
  reasonNote?: string
): PunchEvent {
  return {
    id: `corr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    employeeId,
    type,
    at,
    source: 'office',
    reason,
    reasonNote,
    approvedByEmployeeId,
  };
}

export function getWeekPunchRecords(
  events: PunchEvent[],
  employeeId: EmployeeIdKey,
  weekStart = startOfWeek(new Date(), { weekStartsOn: WEEK_STARTS_ON })
): DayPunchRecord[] {
  return WORK_WEEKDAYS.map((_, index) => {
    const day = addDays(weekStart, index);
    const dateKey = format(day, 'yyyy-MM-dd');
    const dayEvents = getEmployeeEventsForDate(events, employeeId, dateKey);
    const status = buildTodayPunchStatus(events, employeeId, dateKey);
    return {
      dateKey,
      clockIn: status.clockIn,
      clockOut: status.clockOut,
      lunchStart: status.lunchStart,
      lunchEnd: status.lunchEnd,
      events: dayEvents,
    };
  });
}
